import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import LoadingSpinner from '../../components/common/LoadingSpinner'
import axios from 'axios'
import { toast } from 'react-toastify'
import { format, isAfter, isBefore, addDays, parseISO } from 'date-fns'

const HeadOfAcquisitionDashboard = () => {
  const { user } = useAuth()
  const [loading, setLoading] = useState(true)
  const [dashboardData, setDashboardData] = useState({
    totalGyms: 0,
    pendingApprovals: 0,
    completedApprovals: 0,
    recentGyms: [],
    stageBreakdown: {},
    checklistStats: {},
    recentActions: [],
    checklistsAwaitingReview: [],
    upcomingGoLiveDates: []
  })

  // Modal state for metric details
  const [metricModal, setMetricModal] = useState({
    isOpen: false,
    title: '',
    type: null,
    data: [],
    loading: false
  })

  useEffect(() => {
    fetchDashboardData()
  }, [])

  const fetchDashboardData = async () => {
    try {
      setLoading(true)
      
      // Fetch multiple data sources in parallel
      const [gymsResponse, checklistResponse, approvalsResponse, reviewItemsResponse] = await Promise.all([
        axios.get('/api/gyms/stats/overview'),
        axios.get('/api/checklists/stats/overview'),
        axios.get('/api/approvals/stats/overview'),
        axios.get('/api/checklists/review')
      ])

      // Get recent gyms
      const recentGymsResponse = await axios.get('/api/gyms?limit=5')
      
      // Calculate dashboard metrics
      const gymsByStage = gymsResponse.data.gymsByStage || []
      const stageBreakdown = gymsByStage.reduce((acc, stage) => {
        acc[stage.stage] = stage._count
        return acc
      }, {})

      // Calculate gyms in Head of Acquisition scope
      const hoaStages = ['Site Visit', 'Proposal Sent', 'Agreement Signed', 'Facility Work']
      const hoaGymsCount = gymsByStage
        .filter(stage => hoaStages.includes(stage.stage))
        .reduce((total, stage) => total + stage._count, 0)

      // Get checklists awaiting review
      const checklistsAwaitingReview = reviewItemsResponse.data.items || reviewItemsResponse.data || []

      // Get upcoming Go-Live dates (gyms in Post Reki Approved stage)
      const upcomingGoLiveDates = recentGymsResponse.data.gyms?.filter(gym =>
        ['Post Reki Approved'].includes(gym.stage) && gym.launch_date
      ).map(gym => ({
        ...gym,
        launchDate: parseISO(gym.launch_date)
      })).sort((a, b) => a.launchDate - b.launchDate) || []

      setDashboardData({
        totalGyms: gymsResponse.data.totalGyms || 0,
        hoaGymsCount,
        pendingApprovals: approvalsResponse.data?.pending || 0,
        completedApprovals: approvalsResponse.data?.approved || 0,
        recentGyms: recentGymsResponse.data.gyms || [],
        stageBreakdown,
        checklistStats: checklistResponse.data || {},
        thisWeekProgress: calculateWeeklyProgress(recentGymsResponse.data.gyms || []),
        checklistsAwaitingReview,
        upcomingGoLiveDates
      })
    } catch (error) {
      console.error('Error fetching dashboard data:', error)
      toast.error('Failed to load dashboard data')
    } finally {
      setLoading(false)
    }
  }

  const calculateWeeklyProgress = (gyms) => {
    const oneWeekAgo = addDays(new Date(), -7)
    const recentUpdates = gyms.filter(gym => 
      isAfter(new Date(gym.updatedAt), oneWeekAgo)
    ).length
    return recentUpdates
  }

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return isNaN(date) ? 'N/A' : date.toLocaleString();
  };

  const getGreeting = () => {
    const hour = new Date().getHours()
    if (hour < 12) return 'Good Morning'
    if (hour < 17) return 'Good Afternoon'
    return 'Good Evening'
  }

  const getStageColor = (stage) => {
    const colors = {
      'Site Visit': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'Proposal Sent': 'bg-purple-100 text-purple-800 border-purple-200',
      'Agreement Signed': 'bg-green-100 text-green-800 border-green-200',
      'Facility Work': 'bg-blue-100 text-blue-800 border-blue-200',
      'Post Reki Approved': 'bg-indigo-100 text-indigo-800 border-indigo-200',
      'Gym Onboard': 'bg-red-100 text-red-800 border-red-200'
    }
    return colors[stage] || 'bg-gray-100 text-gray-800 border-gray-200'
  }

  // Open metric detail modal
  const openMetricModal = async (type, title) => {
    setMetricModal({ isOpen: true, title, type, data: [], loading: true })

    try {
      let data = []

      switch (type) {
        case 'totalGyms':
          // Fetch all gyms
          const gymsRes = await axios.get('/api/gyms?limit=100')
          data = gymsRes.data.gyms || []
          break

        case 'pendingApprovals':
          // Fetch pending approvals
          const approvalsRes = await axios.get('/api/approvals?status=pending')
          data = approvalsRes.data.approvals || []
          break

        case 'completedThisWeek':
          // Fetch gyms updated this week
          const weekGymsRes = await axios.get('/api/gyms?limit=100')
          const oneWeekAgo = addDays(new Date(), -7)
          data = (weekGymsRes.data.gyms || []).filter(gym =>
            isAfter(new Date(gym.updatedAt), oneWeekAgo)
          )
          break

        case 'myScope':
          // Fetch gyms in HOA stages
          const scopeGymsRes = await axios.get('/api/gyms?limit=100')
          const hoaStages = ['Site Visit', 'Proposal Sent', 'Agreement Signed', 'Facility Work']
          data = (scopeGymsRes.data.gyms || []).filter(gym =>
            hoaStages.includes(gym.stage)
          )
          break

        default:
          data = []
      }

      setMetricModal(prev => ({ ...prev, data, loading: false }))
    } catch (error) {
      console.error('Error fetching metric details:', error)
      toast.error('Failed to load details')
      setMetricModal(prev => ({ ...prev, loading: false }))
    }
  }

  const closeMetricModal = () => {
    setMetricModal({ isOpen: false, title: '', type: null, data: [], loading: false })
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header with Greeting */}
      <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-lg shadow-lg text-white p-6">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-bold">
              {getGreeting()}, {user?.firstName}! 👋
            </h1>
            <p className="mt-2 text-green-100">
              Head of Acquisition Dashboard
            </p>
            <p className="text-sm text-green-200 mt-1">
              Overseeing gym acquisition pipeline from Site Visit to Facility Work
            </p>
          </div>
          <div className="text-right">
            <div className="bg-white bg-opacity-20 rounded-lg px-4 py-2">
              <div className="text-sm text-green-100">Total Pipeline</div>
              <div className="text-2xl font-bold">{dashboardData.hoaGymsCount}</div>
              <div className="text-xs text-green-200">Gyms under review</div>
            </div>
          </div>
        </div>
      </div>

      {/* Notification Bar */}
      {dashboardData.checklistsAwaitingReview.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="w-5 h-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">
                You have {dashboardData.checklistsAwaitingReview.length} checklists to review today
              </h3>
              <div className="mt-2 text-sm text-blue-700">
                <Link to="/checklists" className="font-medium underline hover:text-blue-600">
                  Review now →
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Key Metrics - Clickable */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div
          onClick={() => openMetricModal('totalGyms', 'Total Gyms')}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 cursor-pointer hover:shadow-md hover:border-blue-300 transition-all"
        >
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H3m2 0h4M9 7h6m-6 4h6m-6 4h6" />
                </svg>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Gyms</p>
              <p className="text-2xl font-bold text-gray-900">{dashboardData.totalGyms}</p>
            </div>
          </div>
        </div>

        <div
          onClick={() => openMetricModal('pendingApprovals', 'Pending Approvals')}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 cursor-pointer hover:shadow-md hover:border-orange-300 transition-all"
        >
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Pending Approvals</p>
              <p className="text-2xl font-bold text-gray-900">{dashboardData.pendingApprovals}</p>
            </div>
          </div>
        </div>

        <div
          onClick={() => openMetricModal('completedThisWeek', 'Completed This Week')}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 cursor-pointer hover:shadow-md hover:border-green-300 transition-all"
        >
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Completed This Week</p>
              <p className="text-2xl font-bold text-gray-900">{dashboardData.thisWeekProgress}</p>
            </div>
          </div>
        </div>

        <div
          onClick={() => openMetricModal('myScope', 'My Scope')}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 cursor-pointer hover:shadow-md hover:border-purple-300 transition-all"
        >
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">My Scope</p>
              <p className="text-2xl font-bold text-gray-900">{dashboardData.hoaGymsCount}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <Link
            to="/kanban"
            className="flex items-center p-4 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition-colors group"
          >
            <div className="flex-shrink-0">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-green-900">Kanban</p>
              <p className="text-xs text-green-600">Manage gym pipeline</p>
            </div>
          </Link>

          <Link
            to="/gyms"
            className="flex items-center p-4 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors group"
          >
            <div className="flex-shrink-0">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H3m2 0h4M9 7h6m-6 4h6m-6 4h6" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-blue-900">Gyms</p>
              <p className="text-xs text-blue-600">View all gyms</p>
            </div>
          </Link>

          <Link
            to="/assign-tasks"
            className="flex items-center p-4 bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100 transition-colors group"
          >
            <div className="flex-shrink-0">
              <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-purple-900">Assign Tasks</p>
              <p className="text-xs text-purple-600">Assign facility work</p>
            </div>
          </Link>

          <Link
            to="/checklists/review"
            className="flex items-center p-4 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100 transition-colors group"
          >
            <div className="flex-shrink-0">
              <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-indigo-900">Task Review</p>
              <p className="text-xs text-indigo-600">Review and approve tasks</p>
            </div>
          </Link>

          <Link
            to="/users"
            className="flex items-center p-4 bg-orange-50 border border-orange-200 rounded-lg hover:bg-orange-100 transition-colors group"
          >
            <div className="flex-shrink-0">
              <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-orange-900">Users</p>
              <p className="text-xs text-orange-600">View team members</p>
            </div>
          </Link>

          <Link
            to="/expired-clients"
            className="flex items-center p-4 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors group"
          >
            <div className="flex-shrink-0">
              <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-red-900">Expired Clients</p>
              <p className="text-xs text-red-600">View expired gym clients</p>
            </div>
          </Link>
        </div>
      </div>

      {/* Checklists Awaiting Review Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Checklists Awaiting Your Review</h3>
          <Link
            to="/checklists/review"
            className="text-sm text-green-600 hover:text-green-700 font-medium"
          >
            View all →
          </Link>
        </div>
        
        {dashboardData.checklistsAwaitingReview.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Gym Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Stage
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {dashboardData.checklistsAwaitingReview.slice(0, 5).map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {item.gym?.name || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStageColor(item.gym?.stage)}`}>
                        {item.gym?.stage || 'N/A'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        item.status === 'submitted' ? 'bg-yellow-100 text-yellow-800' :
                        item.status === 'completed' ? 'bg-green-100 text-green-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {item.status === 'submitted' ? 'In Review' :
                         item.status === 'completed' ? 'Completed' :
                         item.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <Link
                        to="/checklists/review"
                        className="text-green-600 hover:text-green-900 font-medium"
                      >
                        Review
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8">
            <div className="text-gray-400 text-4xl mb-2">✅</div>
            <p className="text-gray-500">No checklists awaiting review</p>
          </div>
        )}
      </div>

      {/* Upcoming Go-Live Dates Calendar */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Upcoming Go-Live Dates</h3>
        
        {dashboardData.upcomingGoLiveDates.length > 0 ? (
          <div className="space-y-3">
            {dashboardData.upcomingGoLiveDates.slice(0, 5).map((gym) => (
              <div key={gym.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <p className="font-medium text-gray-900">{gym.name}</p>
                  <p className="text-sm text-gray-600">{gym.city}</p>
                </div>
                <div className="text-right">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStageColor(gym.stage)}`}>
                    {gym.stage}
                  </span>
                  <p className="text-xs text-gray-500 mt-1">
                    {format(gym.launchDate, 'MMM dd, yyyy')}
                  </p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <div className="text-gray-400 text-4xl mb-2">📅</div>
            <p className="text-gray-500">No upcoming Go-Live dates</p>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Gyms */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Gym Updates</h3>
          <div className="space-y-3">
            {dashboardData.recentGyms.length > 0 ? (
              dashboardData.recentGyms.slice(0, 5).map(gym => (
                <div key={gym.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{gym.name}</p>
                    <p className="text-sm text-gray-600">{gym.city}</p>
                  </div>
                  <div className="text-right">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStageColor(gym.stage)}`}>
                      {gym.stage}
                    </span>
                    <p className="text-xs text-gray-500 mt-1">
                      {formatDate(gym.updatedAt)}
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-gray-500 text-center py-4">No recent gym updates</p>
            )}
          </div>
          <div className="mt-4">
            <Link
              to="/gyms"
              className="text-sm text-green-600 hover:text-green-700 font-medium"
            >
              View all gyms →
            </Link>
          </div>
        </div>

        {/* Stage Pipeline Breakdown */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Pipeline Breakdown</h3>
          <div className="space-y-3">
            {['Site Visit', 'Proposal Sent', 'Agreement Signed', 'Facility Work'].map(stage => {
              const count = dashboardData.stageBreakdown[stage] || 0
              const isInScope = true // All these stages are in Head of Acquisition scope
              
              return (
                <div key={stage} className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className={`w-3 h-3 rounded-full mr-3 ${isInScope ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                    <span className="text-sm font-medium text-gray-900">{stage}</span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-lg font-bold text-gray-900 mr-2">{count}</span>
                    <span className="text-xs text-gray-500">gyms</span>
                  </div>
                </div>
              )
            })}
          </div>
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-gray-900">Your Authority Scope</span>
              <span className="text-lg font-bold text-green-600">{dashboardData.hoaGymsCount}</span>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Gyms you can approve and move forward
            </p>
          </div>
        </div>
      </div>

      {/* Authority & Permissions Info */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-6">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-green-800">
              Head of Acquisition Authority
            </h3>
            <div className="mt-2 text-sm text-green-700">
              <ul className="list-disc list-inside space-y-1">
                <li>Review and approve gym transitions from <strong>Site Visit → Facility Work</strong></li>
                <li>Validate checklist items and uploaded proof from BD and Facility teams</li>
                <li>Coordinate with teams through internal notes and feedback</li>
                <li>Full visibility across the acquisition pipeline</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Metric Detail Modal */}
      {metricModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">{metricModal.title}</h2>
              <button
                onClick={closeMetricModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {metricModal.loading ? (
                <div className="flex justify-center items-center py-12">
                  <LoadingSpinner size="lg" />
                </div>
              ) : metricModal.data.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-gray-400 text-5xl mb-4">📋</div>
                  <p className="text-gray-500">No data available</p>
                </div>
              ) : metricModal.type === 'pendingApprovals' ? (
                // Pending Approvals Table
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Gym</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stage</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Requested</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {metricModal.data.map((approval) => (
                        <tr key={approval.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">
                            {approval.gym?.name || 'N/A'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {approval.type || 'Stage Transition'}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStageColor(approval.gym?.stage)}`}>
                              {approval.gym?.stage || 'N/A'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500">
                            {formatDate(approval.createdAt)}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <Link
                              to={`/gyms/${approval.gymId}`}
                              className="text-green-600 hover:text-green-800 font-medium"
                              onClick={closeMetricModal}
                            >
                              View
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                // Gyms Table (for totalGyms, completedThisWeek, myScope)
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Gym Name</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stage</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Updated</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {metricModal.data.map((gym) => (
                        <tr key={gym.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">
                            {gym.name}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {gym.city}{gym.state ? `, ${gym.state}` : ''}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStageColor(gym.stage)}`}>
                              {gym.stage}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500">
                            {formatDate(gym.updatedAt)}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <Link
                              to={`/gyms/${gym.id}`}
                              className="text-green-600 hover:text-green-800 font-medium"
                              onClick={closeMetricModal}
                            >
                              View
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="border-t border-gray-200 p-4 bg-gray-50 rounded-b-xl">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-500">
                  {metricModal.data.length} {metricModal.data.length === 1 ? 'item' : 'items'}
                </span>
                <button
                  onClick={closeMetricModal}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm font-medium"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default HeadOfAcquisitionDashboard 